import { create } from 'zustand';

/**
 * Live Discord voice state for the current Activity voice channel, keyed by
 * Discord user id. Fed ONLY by the ActivityBridge voice subscription (client↔
 * Discord; the Ringmaster server never sees this). Player bubbles read a single
 * primitive out of here to render the talking glow / muted-mic indicators.
 *
 * IMPORTANT: bubbles must select PRIMITIVES (booleans), never a fresh object —
 * object-returning selectors trip React error #185 (see CLAUDE.md). Use the
 * `useIsSpeaking` / `useIsMuted` hooks below, which read one boolean each.
 */
interface VoiceState {
  /** True once we know live voice is obtainable (scope granted + channel present). */
  available: boolean;
  /** discordUserId → currently speaking. */
  speaking: Record<string, boolean>;
  /** discordUserId → mic muted (self-mute or server-mute). */
  muted: Record<string, boolean>;
  setAvailable(available: boolean): void;
  setSpeaking(userId: string, speaking: boolean): void;
  setMuted(userId: string, muted: boolean): void;
  /** Drop all voice state (on unmount / channel change). */
  reset(): void;
}

export const useVoiceStore = create<VoiceState>((set) => ({
  available: false,
  speaking: {},
  muted: {},
  setAvailable: (available) => set({ available }),
  setSpeaking: (userId, speaking) =>
    set((s) =>
      s.speaking[userId] === speaking ? s : { speaking: { ...s.speaking, [userId]: speaking } },
    ),
  setMuted: (userId, muted) =>
    set((s) => (s.muted[userId] === muted ? s : { muted: { ...s.muted, [userId]: muted } })),
  reset: () => set({ available: false, speaking: {}, muted: {} }),
}));

/**
 * Is this Discord user speaking right now? Returns false when voice is
 * unavailable, the id is absent/undefined, or off a non-Activity surface. Selects
 * a single boolean (no fresh objects → no React #185).
 */
export function useIsSpeaking(discordUserId: string | null | undefined): boolean {
  return useVoiceStore((s) => (discordUserId ? s.available && !!s.speaking[discordUserId] : false));
}

/** Is this Discord user's mic muted? Same gating/rules as {@link useIsSpeaking}. */
export function useIsMuted(discordUserId: string | null | undefined): boolean {
  return useVoiceStore((s) => (discordUserId ? s.available && !!s.muted[discordUserId] : false));
}
